import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Map, Target, Sparkles, Loader2, PlusCircle, CheckCircle2, PartyPopper, Star, X as XIcon, MessageCircle, ArrowRight } from "lucide-react";
import client from "../lib/api";
import { RiverMap } from "./roadmap/RiverMap";
import { TaskDrawer } from "./roadmap/TaskDrawer";


/**
 * KLA-043: the conflict conversation. When a goal is a hard turn from
 * the person's current course, Klair reasons first and builds nothing
 * until they pick a path with open eyes.
 */
function ConflictGate({ conflict, busy, onPick }) {
  const paras = conflict?.message || [];
  const paths = [conflict?.path_a, conflict?.path_b].filter(Boolean);
  return (
    <div className="k-card" data-testid="roadmap-conflict-gate">
      <div className="k-label mb-3 flex items-center gap-2">
        <MessageCircle size={13} /> before any roadmap, a real talk
      </div>
      <div className="space-y-3 mb-6">
        {paras.map((p, i) => (
          <p
            key={i}
            className="klareet-serif"
            style={{ fontSize: i === 0 ? "1.15rem" : "1.02rem", color: "hsl(var(--klareet-ink))", lineHeight: 1.55 }}
            data-testid={`conflict-para-${i}`}
          >
            {p}
          </p>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {paths.map((p) => (
          <div
            key={p.key}
            className="rounded-2xl p-5 flex flex-col gap-3"
            style={{ background: "hsl(var(--klareet-canvas-2))", border: "1px solid hsl(var(--klareet-line-soft))" }}
            data-testid={`conflict-path-${p.key}`}
          >
            <div className="font-semibold" style={{ color: "hsl(var(--klareet-ink))" }}>{p.title}</div>
            <div className="k-quiet !text-sm" style={{ lineHeight: 1.55, flex: 1 }}>{p.summary}</div>
            <button
              onClick={() => onPick(p.key)}
              disabled={busy}
              className="k-btn k-btn-primary !text-sm w-fit"
              data-testid={`conflict-pick-${p.key}`}
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />} Build this path
            </button>
          </div>
        ))}
      </div>
      <p className="k-quiet !text-xs mt-4">No judgement either way. You can come back and switch paths later.</p>
    </div>
  );
}

const PRESETS = [4, 8, 12];

function firstNotDone(phases) {
  for (let i = 0; i < (phases || []).length; i++) {
    if (phases[i].status !== "done") return i;
  }
  return Math.max(0, (phases || []).length - 1);
}

/** Inline goal creation, used both when there is no goal yet and to add
 *  another goal from the roadmap. A new goal is set as the driving goal
 *  so the whole app, including this roadmap, unifies around it. */
function NewGoalForm({ onCreate, onCancel, busy }) {
  const [title, setTitle] = useState("");
  const [weeks, setWeeks] = useState(8);
  return (
    <div className="k-card" data-testid="roadmap-new-goal-form">
      <div className="k-label mb-3">name your goal</div>
      <textarea
        className="k-input klareet-serif"
        rows={2}
        maxLength={200}
        placeholder="what do you want to make happen?"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        style={{ fontSize: "1.25rem", lineHeight: 1.35 }}
        data-testid="roadmap-new-goal-title"
      />
      <div className="k-label mt-5 mb-2">give it a timeline</div>
      <div className="flex flex-wrap gap-2" data-testid="roadmap-new-goal-weeks">
        {PRESETS.map((w) => (
          <button
            key={w}
            type="button"
            onClick={() => setWeeks(w)}
            data-testid={`roadmap-new-goal-weeks-${w}`}
            className="px-4 py-2 rounded-full text-sm font-medium border transition-all duration-200 hover:-translate-y-0.5"
            style={{
              background: weeks === w ? "hsl(var(--klareet-ink))" : "hsl(var(--klareet-canvas-2))",
              color: weeks === w ? "#fff" : "hsl(var(--klareet-ink))",
              borderColor: weeks === w ? "hsl(var(--klareet-ink))" : "hsl(var(--klareet-line-soft))",
            }}
          >
            {w} weeks
          </button>
        ))}
      </div>
      <div className="flex justify-end gap-2 mt-6">
        {onCancel && <button onClick={onCancel} className="k-btn k-btn-ghost !text-sm" data-testid="roadmap-new-goal-cancel">Cancel</button>}
        <button onClick={() => onCreate(title.trim(), weeks)} disabled={!title.trim() || busy} className="k-btn k-btn-primary !text-sm" data-testid="roadmap-new-goal-submit">
          {busy ? <><Loader2 size={14} className="animate-spin" /> Building.</> : <><Sparkles size={14} /> Create and map it</>}
        </button>
      </div>
    </div>
  );
}

/**
 * RoadmapScreen.
 *
 * Goal-driven and unified around the one driving goal. On open it lands
 * on the driving goal (unless the URL names a specific one), so the
 * left-nav Roadmap always matches the goal steering the rest of the app.
 * You can switch goals, make any goal the driving one, or add a new goal
 * inline. Each stop opens a drawer where Klair keeps you accountable.
 */
export default function RoadmapScreen({ product }) {
  const base = product === "grow" ? "/grow" : "";
  const [params, setParams] = useSearchParams();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(params.get("goal") || null);
  const [roadmap, setRoadmap] = useState(null);
  const [rmLoading, setRmLoading] = useState(false);
  const [drawer, setDrawer] = useState({ open: false, phase: null });
  const [report, setReport] = useState(null);
  const [creating, setCreating] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);

  const loadGoals = async (selectId) => {
    const r = await client.get("/engagement/goal");
    const items = r?.data?.items || [];
    setGoals(items);
    const want = selectId || params.get("goal");
    const driving = items.find((g) => g.is_driving);
    const sel = items.find((g) => g.id === want) ? want : (driving?.id || items[0]?.id || null);
    setActiveId(sel);
    return items;
  };

  useEffect(() => {
    let cached = null;
    try { const raw = localStorage.getItem("klareet.report"); if (raw) cached = JSON.parse(raw); } catch (_e) { /* ignore */ }
    if (cached) {
      setReport(cached);
    } else {
      client.get(`/demo/read/latest?product=${product}`)
        .then((r) => { const f = r?.data?.report; if (f) { setReport(f); try { localStorage.setItem("klareet.report", JSON.stringify(f)); } catch (_e) { /* ignore */ } } })
        .catch(() => { /* no read yet */ });
    }
    loadGoals().catch(() => setGoals([])).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const active = useMemo(() => goals.find((g) => g.id === activeId) || null, [goals, activeId]);

  useEffect(() => {
    if (!activeId) { setRoadmap(null); return; }
    setRmLoading(true);
    client.get(`/engagement/goal/${activeId}/roadmap`)
      .then((r) => setRoadmap(r?.data || null))
      .catch(() => setRoadmap(null))
      .finally(() => setRmLoading(false));
  }, [activeId]);

  const selectGoal = (id) => { setActiveId(id); setParams(id ? { goal: id } : {}); };

  const makeDriving = async (id) => {
    try {
      await client.post(`/engagement/goal/${id}/drive`);
      setGoals((gs) => gs.map((g) => ({ ...g, is_driving: g.id === id })));
    } catch (_e) { /* silent */ }
  };

  const createGoal = async (title, weeks) => {
    if (!title) return;
    setCreateBusy(true);
    try {
      const { data } = await client.post("/engagement/goal", { title, timeline_weeks: weeks || 8 });
      await client.post(`/engagement/goal/${data.id}/drive`).catch(() => {});
      await client.post(`/engagement/goal/${data.id}/gap-analysis?warm=false`).catch(() => {});
      await loadGoals(data.id);
      setParams({ goal: data.id });
      setCreating(false);
    } catch (_e) { /* silent */ }
    finally { setCreateBusy(false); }
  };

  const phases = roadmap?.phases || [];
  const needsPath = !!roadmap?.needs_path;
  const currentIndex = firstNotDone(phases);
  const doneCount = phases.filter((p) => p.status === "done").length;
  const allDone = phases.length > 0 && doneCount === phases.length;

  const pickConflictPath = async (path) => {
    setRmLoading(true);
    try {
      const { data } = await client.post(`/engagement/goal/${activeId}/roadmap/build`, { path });
      setRoadmap(data || null);
    } catch (_e) { /* silent */ }
    finally { setRmLoading(false); }
  };

  const openPhase = (phase) => setDrawer({ open: true, phase });
  const onPhaseChanged = (updated) => {
    setRoadmap((rm) => (rm ? { ...rm, phases: rm.phases.map((p) => (p.id === updated.id ? updated : p)) } : rm));
    setDrawer((d) => (d.phase && d.phase.id === updated.id ? { ...d, phase: updated } : d));
  };

  if (loading) {
    return <div className="max-w-3xl mx-auto px-5 pt-24 text-center" data-testid="roadmap-loading"><Loader2 size={24} className="animate-spin mx-auto" style={{ color: "hsl(var(--klareet-muted))" }} /></div>;
  }

  // No goal yet: a roadmap needs a goal. Offer inline creation.
  if (goals.length === 0) {
    const ideas = report?.roadmap || [];
    return (
      <div className="max-w-2xl mx-auto px-5 pt-16 pb-24" data-testid="roadmap-no-goal">
        <div className="k-label mb-2">your roadmap</div>
        <h1 className="klareet-serif mb-3" style={{ fontSize: "clamp(2rem, 4vw, 2.8rem)", letterSpacing: "-0.02em", lineHeight: 1.1 }}>
          A roadmap needs a goal.
        </h1>
        <p className="k-quiet mb-6" style={{ maxWidth: 520, lineHeight: 1.6 }}>
          Name a goal and Klair reads it against your snapshot, then lays out a plan you can actually follow, one stop at a time, and keeps you honest as you go.
        </p>
        <NewGoalForm onCreate={createGoal} busy={createBusy} />

        {ideas.length > 0 && (
          <div className="mt-10">
            <div className="k-label mb-3 flex items-center gap-2"><Sparkles size={13} /> ideas from your snapshot</div>
            <div className="space-y-2">
              {ideas.slice(0, 4).map((it, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => createGoal(it.theme, 8)}
                  className="w-full text-left rounded-xl p-4 transition-all hover:-translate-y-0.5"
                  style={{ background: "hsl(var(--klareet-canvas-2))", border: "1px solid hsl(var(--klareet-line-soft))" }}
                  data-testid={`roadmap-idea-${i}`}
                >
                  <div className="klareet-mono !text-[10px]" style={{ color: "hsl(var(--klareet-muted))" }}>week {it.week_from_start}</div>
                  <div className="font-medium mt-0.5" style={{ color: "hsl(var(--klareet-ink))" }}>{it.theme}</div>
                  {it.action && <div className="k-quiet !text-xs mt-1">{it.action}</div>}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-5 pt-10 pb-24" data-testid="roadmap-screen">
      {/* Goal selector + add */}
      <div className="flex flex-wrap gap-2 mb-6 items-center" data-testid="roadmap-goal-selector">
        {goals.map((g) => (
          <button
            key={g.id}
            onClick={() => selectGoal(g.id)}
            data-testid={`roadmap-goal-${g.id}`}
            className="px-4 py-2 rounded-full text-sm border transition-all duration-200 hover:-translate-y-0.5 flex items-center gap-2 max-w-[260px]"
            style={{
              background: activeId === g.id ? "hsl(var(--klareet-ink))" : "hsl(var(--klareet-canvas-2))",
              color: activeId === g.id ? "#fff" : "hsl(var(--klareet-ink))",
              borderColor: activeId === g.id ? "hsl(var(--klareet-ink))" : "hsl(var(--klareet-line-soft))",
            }}
          >
            {g.is_driving ? <Star size={13} fill="currentColor" /> : <Target size={13} />}
            <span className="truncate">{g.title}</span>
          </button>
        ))}
        {goals.length < 3 && (
          <button
            onClick={() => setCreating((c) => !c)}
            className="px-4 py-2 rounded-full text-sm border border-dashed transition-all duration-200 hover:-translate-y-0.5 flex items-center gap-2"
            style={{ background: "transparent", color: "hsl(var(--klareet-ink-soft))", borderColor: "hsl(var(--klareet-line))" }}
            data-testid="roadmap-add-goal-btn"
          >
            {creating ? <XIcon size={13} /> : <PlusCircle size={13} />} {creating ? "Cancel" : "New goal"}
          </button>
        )}
      </div>

      {creating && (
        <div className="mb-6">
          <NewGoalForm onCreate={createGoal} onCancel={() => setCreating(false)} busy={createBusy} />
        </div>
      )}

      {/* Header */}
      <div className="k-label mb-2 flex items-center gap-2"><Map size={13} /> the roadmap for</div>
      <h1 className="klareet-serif mb-3" style={{ fontSize: "clamp(1.9rem, 4vw, 2.9rem)", letterSpacing: "-0.02em", lineHeight: 1.1 }} data-testid="roadmap-goal-title">
        {active?.title}
      </h1>
      <div className="flex items-center gap-3 flex-wrap mb-8">
        <span className="klareet-mono !text-xs k-chip"><CheckCircle2 size={12} /> {doneCount} of {phases.length} stops done</span>
        {active?.timeline_weeks && <span className="klareet-mono !text-xs" style={{ color: "hsl(var(--klareet-muted))" }}>{active.timeline_weeks} week plan</span>}
        {active?.is_driving ? (
          <span className="k-chip k-chip-teal !text-xs" data-testid="roadmap-driving-badge"><Star size={11} fill="currentColor" /> driving your app</span>
        ) : (
          <button
            onClick={() => makeDriving(active.id)}
            className="k-btn k-btn-ghost !py-1 !px-2.5 !text-xs"
            data-testid="roadmap-make-driving"
          >
            <Star size={11} /> Make this the driving goal
          </button>
        )}
      </div>

      {roadmap?.grounding_line && (
        <p
          className="klareet-serif mb-6 rounded-xl p-4"
          style={{ fontSize: "1.02rem", color: "hsl(var(--klareet-ink))", lineHeight: 1.55, background: "hsl(var(--klareet-amber) / 0.10)", border: "1px solid hsl(var(--klareet-amber) / 0.28)" }}
          data-testid="roadmap-grounding-line"
        >
          {roadmap.grounding_line}
        </p>
      )}

      {rmLoading ? (
        <div className="text-center py-16" data-testid="roadmap-building">
          <Loader2 size={22} className="animate-spin mx-auto mb-2" style={{ color: "hsl(var(--klareet-muted))" }} />
          <div className="k-quiet !text-sm">Klair is laying out your river.</div>
        </div>
      ) : needsPath ? (
        <ConflictGate conflict={roadmap?.conflict} busy={rmLoading} onPick={pickConflictPath} />
      ) : phases.length === 0 ? (
        <div className="k-card text-center py-12" data-testid="roadmap-empty">
          <p className="k-quiet mb-4">This goal does not have a plan yet.</p>
          <button
            onClick={() => { setRmLoading(true); client.post(`/engagement/goal/${activeId}/roadmap/build`).then((r) => setRoadmap(r.data)).finally(() => setRmLoading(false)); }}
            className="k-btn k-btn-primary !text-sm"
            data-testid="roadmap-build-btn"
          >
            <Sparkles size={14} /> Build the roadmap
          </button>
        </div>
      ) : (
        <>
          {allDone && (
            <div
              className="rounded-2xl p-5 mb-6 flex items-start gap-3"
              style={{ background: "hsl(var(--klareet-teal) / 0.09)", border: "1px solid hsl(var(--klareet-teal) / 0.28)" }}
              data-testid="roadmap-complete"
            >
              <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "hsl(var(--klareet-teal))", color: "#fff" }}>
                <PartyPopper size={18} />
              </span>
              <div>
                <div className="klareet-serif" style={{ fontSize: "1.2rem", color: "hsl(var(--klareet-ink))", lineHeight: 1.3 }}>
                  You reached every stop on this one.
                </div>
                <div className="k-quiet !text-sm mt-1">That is a real thing, and Klair saw you do it. Set the next goal whenever you are ready.</div>
              </div>
            </div>
          )}
          <RiverMap phases={phases} currentIndex={currentIndex} onOpen={openPhase} />
        </>
      )}

      <p className="k-quiet !text-xs mt-8 flex items-center gap-1.5" data-testid="roadmap-footer">
        Tap any stop to open it. Klair keeps this private to you, and holds you to it, gently.
      </p>

      <TaskDrawer
        open={drawer.open}
        onOpenChange={(v) => setDrawer((d) => ({ ...d, open: v }))}
        goalId={activeId}
        phase={drawer.phase}
        onChanged={onPhaseChanged}
      />
    </div>
  );
}
